import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star, Zap, Truck, ShieldCheck, Tag } from "lucide-react";
import { fetchCategories, fetchProducts, getViewHistory } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { waGeneralLink } from "@/lib/whatsapp";
import CategoryCard from "@/components/CategoryCard";
import ProductCard from "@/components/ProductCard";
import CampaignBanner from "@/components/CampaignBanner";
import { supabase, getStorageUrl } from "@/lib/supabase";

export default function Home() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [history, setHistory] = useState([]);
  const [heroImgs, setHeroImgs] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [discountText, setDiscountText] = useState("50% OFF<br/>SALE");

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => setCats([]));
    fetchProducts({ featured: true }).then(setFeatured).catch(() => setFeatured([]));

    supabase.storage.from("images").list("posters", {
      limit: 50,
      sortBy: { column: "created_at", order: "desc" }
    }).then(({ data, error }) => {
      if (!error && data && data.length > 0) {
        const list = data
          .filter(f => f.name !== ".emptyFolderPlaceholder")
          .map(f => getStorageUrl(`posters/${f.name}`));
        if (list.length > 0) setHeroImgs(list);
      }
    });

    supabase.storage.from("images").download("settings/hero-sticker.json")
      .then(({ data, error }) => {
        if (!error && data) {
          data.text().then(text => {
            try {
              const json = JSON.parse(text);
              if (json.discountText) setDiscountText(json.discountText);
            } catch(e) {}
          });
        }
      });
    if (user) {
      getViewHistory(user.id).then(setHistory).catch(() => setHistory([]));
    } else {
      setHistory([]);
    }
  }, [user]);

  // Auto-scroll slider
  useEffect(() => {
    if (heroImgs.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImgs.length);
    }, 4000); // 4 seconds per slide
    return () => clearInterval(timer);
  }, [heroImgs]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[3px] border-black candy-dots">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 relative">
            <div className="inline-flex items-center gap-2 nb-pill mb-5" data-testid="hero-tag">
              <Sparkles size={16} strokeWidth={3} />
              <span>FRESH DROP • MEN'S COLLECTION</span>
            </div>
            <h1 className="font-display font-black uppercase text-5xl md:text-7xl lg:text-[88px] leading-[0.9] tracking-tight">
              Sweet Fits.<br/>
              <span className="bg-[var(--primary)] text-white px-3 inline-block -rotate-1 my-2">Loud Vibes.</span><br/>
              <span className="bg-[var(--secondary)] px-3 inline-block rotate-1">Premium Picks.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl max-w-xl font-medium text-black/80">
              From candy-bright tees to crisp formal shirts, watches, kicks &amp; more — Khasim Trendz is Machilipatnam's playground for premium menswear.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-button" className="nb-btn bg-[var(--primary)] text-white inline-flex items-center gap-2">
                Shop The Drop <ArrowRight strokeWidth={3} />
              </Link>
              <a
                href={waGeneralLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-wa-button"
                className="nb-btn bg-white text-black inline-flex items-center gap-2"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[36px] nb-border overflow-hidden bg-[var(--candy-blue)] nb-shadow-lg flex">
              {heroImgs.length > 0 ? (
                <div 
                  className="flex w-full h-full transition-transform duration-700 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {heroImgs.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`Hero poster ${i + 1}`}
                      className="w-full h-full object-contain shrink-0"
                    />
                  ))}
                </div>
              ) : (
                <img
                  src="https://images.pexels.com/photos/15694146/pexels-photo-15694146.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=850&w=940"
                  alt="Hero menswear"
                  className="w-full h-full object-contain"
                />
              )}
            </div>
            {/* Stickers */}
            <div 
              className="sticker w-24 h-24 -top-6 -left-6 bg-[var(--secondary)] text-black text-center text-sm leading-tight wiggle flex flex-col justify-center items-center"
              dangerouslySetInnerHTML={{ __html: discountText }}
            />
            <div className="sticker w-28 h-28 -bottom-6 -right-4 bg-[var(--accent)] text-white text-center text-xs leading-tight" style={{ transform: "rotate(12deg)" }}>
              <div>
                <div className="text-xl">NEW</div>
                <div>2026</div>
                <div>DROP</div>
              </div>
            </div>
            <div className="sticker w-20 h-20 top-1/2 -right-6 bg-white" style={{ transform: "rotate(-15deg)" }}>
              <Star size={28} strokeWidth={3} fill="#FF3366" color="#FF3366" />
            </div>
          </div>
        </div>
      </section>

      {/* USP STRIP */}
      <section className="bg-black text-white border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {[
            { icon: <Zap strokeWidth={2.5} />, label: "Fresh Weekly" },
            { icon: <Truck strokeWidth={2.5} />, label: "Local Delivery" },
            { icon: <ShieldCheck strokeWidth={2.5} />, label: "Premium Quality" },
            { icon: <Tag strokeWidth={2.5} />, label: "Best Prices" },
          ].map((u, i) => (
            <div key={i} className="flex items-center gap-3 font-display font-black uppercase tracking-wide">
              <span className="w-10 h-10 rounded-full bg-[var(--secondary)] text-black flex items-center justify-center">
                {u.icon}
              </span>
              <span className="text-sm md:text-base">{u.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ACTIVE CAMPAIGN — auto-hides if none live */}
      <CampaignBanner />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="categories-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="nb-pill inline-flex items-center gap-2 mb-4"><Sparkles size={14} strokeWidth={3} /> SHOP BY CATEGORY</div>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight">Pick Your Flavour</h2>
          </div>
          <Link to="/shop" className="nb-btn bg-[var(--secondary)] text-black">View All</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          {cats.map((c, i) => (
            <CategoryCard key={c.slug} category={c} index={i} />
          ))}
        </div>
      </section>

      {/* RECENTLY VIEWED */}
      {user && history.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 border-t-[3px] border-black bg-[var(--secondary)]/10" data-testid="history-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="nb-pill inline-flex items-center gap-2 mb-4 bg-white"><Sparkles size={14} strokeWidth={3} /> PICK UP WHERE YOU LEFT OFF</div>
              <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight">Recently Viewed</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {history.map((h, i) => (
              <ProductCard key={h.id} product={h.product} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 border-t-[3px] border-black checker-bg" data-testid="featured-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="nb-pill inline-flex items-center gap-2 mb-4"><Star size={14} strokeWidth={3} /> HAND-PICKED</div>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight">Top of the pop</h2>
          </div>
          <Link to="/shop" className="nb-btn bg-[var(--primary)] text-white">All Products</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="relative">
          <div className="aspect-[4/3] rounded-[36px] nb-border overflow-hidden nb-shadow-lg">
            <img src="https://images.unsplash.com/photo-1593030761757-71fae45fa0e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Inside the menswear store" className="w-full h-full object-cover" />
          </div>
          <div className="sticker w-28 h-28 -bottom-6 -right-6 bg-[var(--secondary)] text-black wiggle">
            <div className="text-center text-sm leading-tight">
              <div className="text-2xl">EST.</div>
              <div>BUTTAIPET</div>
            </div>
          </div>
        </div>
        <div>
          <div className="nb-pill inline-block mb-4">OUR STORY</div>
          <h2 className="font-display font-black uppercase text-4xl md:text-6xl tracking-tight mb-5">Style with a side of sugar.</h2>
          <p className="text-lg text-black/80 mb-6 max-w-xl">
            Khasim Trendz is the local favourite in Machilipatnam — curating premium menswear that pops. From everyday staples to statement accessories, we hand-pick fits that make you stand out.
          </p>
          <Link to="/about" className="nb-btn bg-[var(--candy-purple)] text-white inline-flex items-center gap-2">
            Read Our Story <ArrowRight strokeWidth={3} />
          </Link>
        </div>
      </section>
    </div>
  );
}
